import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import type { FC, ReactNode } from "react";

import { CalloutBanner } from "tw/components/molecules/CalloutBanner";

type CalloutBannerContextType = {
  isBannerOpen: boolean;
  showBanner: (message: string) => void;
  closeBanner: () => void;
};

const CalloutBannerContext = createContext<CalloutBannerContextType>({
  isBannerOpen: false,
  showBanner: () => {},
  closeBanner: () => {},
});

export const CalloutBannerProvider: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [scheme, setScheme] = useState<"info" | "warning">("info");
  const [message, setMessage] = useState<string | null>(null);

  const showBanner = useCallback(
    (newMessage: string, newScheme: "info" | "warning" = "info") => {
      setMessage(newMessage);
      setScheme(newScheme);
    },
    []
  );

  const closeBanner = useCallback(() => {
    setMessage(null);
  }, []);

  const contextValues = useMemo(
    () => ({ isBannerOpen: !!message, showBanner, closeBanner }),
    [message, showBanner, closeBanner]
  );

  return (
    <CalloutBannerContext.Provider value={contextValues}>
      <div>
        {message && (
          <CalloutBanner
            message={message}
            scheme={scheme}
            close={closeBanner}
          />
        )}
        {children}
      </div>
    </CalloutBannerContext.Provider>
  );
};

export const useCalloutBanner = () => useContext(CalloutBannerContext);
